import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { platformCheckInVisibilityToEnum } from "@mapmycustomers/shared/enum/CheckInVisibility";
import Organization from "@mapmycustomers/shared/types/Organization";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import SharedActivityVisibility from "@app/scene/settings/component/OrganizationDetails/component/Activities/component/FormItems/SharedActivityVisibility";
import ActivitiesSettings from "@app/scene/settings/type/ActivitiesSettings";
import {
  doesOrganizationSupportEmailService,
  getOrganizationId,
  getOrganizationSettings,
  isActiveCheckInsEnabled,
  isCurrentUserOwner,
} from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import useSetting from "@app/util/hook/useSetting";

import EmailFeatureInfo from "./component/EmailFeatureInfo/EmailFeatureInfo";
import ActiveCheckIn from "./component/FormItems/ActiveCheckIn";
import ActiveCheckInAccess from "./component/FormItems/ActiveCheckInAccess";
import CheckInRadius from "./component/FormItems/CheckInRadius";
import CheckItVerifiedVisibility from "./component/FormItems/CheckItVerifiedVisibility";
import NearbyRecordSuggestion from "./component/FormItems/NearbyRecordSuggestion";
import useHandleFinish from "./util/useHandleFinish";

interface Props {
  activeCheckInsEnabled: boolean;
  form: FormInstance<ActivitiesSettings>;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: Organization["id"];
  organizationSettings: Organization["settings"];
  setDirty: () => void;
  showEmailFeatureInfo: boolean;
  unsetDirty: () => void;
}

const Activities: React.FC<Props> = ({
  activeCheckInsEnabled,
  form,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
  setDirty,
  showEmailFeatureInfo,
  unsetDirty,
}) => {
  const intl = useIntl();
  const handleSetDirty = useCallback(() => {
    setDirty();
  }, [setDirty]);
  const activeCheckInSetting = useSetting(
    organizationSettings,
    OrganizationSetting.ACTIVE_CHECK_IN
  );
  const activeCheckInAccessSetting = useSetting(
    organizationSettings,
    OrganizationSetting.ACTIVE_CHECK_IN_ACCESS
  );
  const activityCompletionSetting = useSetting(
    organizationSettings,
    OrganizationSetting.ACTIVITY_COMPLETION_STATUS
  );
  const activityVisibilitySetting = useSetting(
    organizationSettings,
    OrganizationSetting.ACTIVITY_VISIBILITY
  );
  const checkInRadiusSetting = useSetting(
    organizationSettings,
    OrganizationSetting.CHECK_IN_RADIUS
  );
  const checkInVerifiedVisibilitySetting = useSetting(
    organizationSettings,
    OrganizationSetting.CHECK_IN_VERIFIED_VISIBILITY
  );
  const crossObjectActivityVisibilitySetting = useSetting(
    organizationSettings,
    OrganizationSetting.CROSS_OBJECT_ACTIVITY_VISIBILITY
  );
  const nearbySuggestionActivityTypeSetting = useSetting(
    organizationSettings,
    OrganizationSetting.NEAR_BY_SUGGESTION_ACTIVITY_TYPE
  );

  const handleSubmit = useHandleFinish(
    form,
    intl,
    onUpdateOrganization,
    unsetDirty,
    organizationId,
    activeCheckInSetting,
    activeCheckInAccessSetting,
    activityCompletionSetting,
    activityVisibilitySetting,
    checkInRadiusSetting,
    checkInVerifiedVisibilitySetting,
    crossObjectActivityVisibilitySetting,
    nearbySuggestionActivityTypeSetting
  );

  const activeCheckInEnabled = useWatch(OrganizationSetting.ACTIVE_CHECK_IN, form);

  return (
    <ExtendedAnalytics keyPath={["Activities"]}>
      <Form
        form={form}
        initialValues={{
          activeCheckIn: activeCheckInSetting?.value,
          activeCheckInAccess: activeCheckInAccessSetting?.value,
          activityCompletionStatus: activityCompletionSetting?.value,
          activityVisibility: activityVisibilitySetting?.value,
          checkInRadius: checkInRadiusSetting?.value,
          checkInVerifiedVisibility: checkInVerifiedVisibilitySetting?.value
            ? platformCheckInVisibilityToEnum[checkInVerifiedVisibilitySetting?.value]
            : undefined,
          crossObjActivityVisibility: crossObjectActivityVisibilitySetting?.value,
          nearbySuggestionActivityType: nearbySuggestionActivityTypeSetting?.value,
        }}
        onFieldsChange={handleSetDirty}
        onFinish={handleSubmit}
      >
        <Row gutter={[0, layout.spacerL]}>
          <Col sm={{ span: 24 }} xl={{ span: 18 }}>
            <SharedActivityVisibility />
            {activeCheckInsEnabled && (
              <>
                <Form.Item name={OrganizationSetting.ACTIVE_CHECK_IN}>
                  <ActiveCheckIn />
                </Form.Item>
                {activeCheckInEnabled && (
                  <Form.Item name={OrganizationSetting.ACTIVE_CHECK_IN_ACCESS}>
                    <ActiveCheckInAccess />
                  </Form.Item>
                )}
              </>
            )}
            <CheckItVerifiedVisibility intl={intl} />
            <CheckInRadius />
            <Form.Item name={OrganizationSetting.NEAR_BY_SUGGESTION_ACTIVITY_TYPE}>
              <NearbyRecordSuggestion />
            </Form.Item>
            {showEmailFeatureInfo && <EmailFeatureInfo />}
          </Col>
        </Row>
      </Form>
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeCheckInsEnabled: isActiveCheckInsEnabled(state),
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
  showEmailFeatureInfo: isCurrentUserOwner(state) && !doesOrganizationSupportEmailService(state),
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Activities);
